/**
 * @file TransferItemFields.tsx
 * @description Поля строки перемещения после выбора фасовки: партия, «Откуда»,
 * «Куда» и количество (phase19.24 этап 1, шаг 1).
 *
 * Выделены из `TransferItemForm` по правилу композиции: форма из трёх и более
 * секций дробится на компоненты с общим контрактом `value/onChange` (образец —
 * `CarrierMainFields` + `CarrierDeliveryFields`). Форме остались состояние,
 * фокус и отправка.
 *
 * Блок целиком скрыт на `<lg`, пока фасовка не выбрана: на телефоне панель
 * свёрнута до поля скана, и список строк документа начинается сразу под ним.
 */

import { TransferQuantityField } from "./TransferQuantityField";
import {
  TransferTargetFields,
  type TransferTargetMode,
} from "./TransferTargetFields";
import {
  formatTransferLot,
  type TransferStockLot,
} from "./useTransferStockOptions";
import { Combobox } from "../../ui/combobox";
import { WmsFieldLabel } from "../WmsFieldLabel";
import type { StorageBinDto } from "../../../lib/api-bins";
import type { WarehouseDto } from "../../../lib/api-wms";

interface Props {
  /** Партии выбранной фасовки; пусто — фасовка ещё не выбрана. */
  lots: TransferStockLot[];
  /** Партии, уже стоящие в документе: одна партия — одна строка. */
  usedLotIds: string[];
  lotId: string;
  onLotChange: (lotId: string) => void;
  /** Ref триггера «Партии»: форма уводит сюда фокус после скана артикула. */
  lotTriggerRef: React.Ref<HTMLButtonElement>;
  /** Выбранная партия — её адрес показываем в «Откуда». */
  lot: TransferStockLot | null;
  hasSku: boolean;
  fromWarehouseName: string;
  mode: TransferTargetMode;
  onModeChange: (mode: TransferTargetMode) => void;
  binId: string;
  onBinChange: (binId: string) => void;
  toWarehouseId: string;
  onWarehouseChange: (id: string) => void;
  bins: StorageBinDto[];
  /** Склад-источник — резолвер скана ячейки (phase19.22). */
  fromWarehouseId?: string;
  targetWarehouses: WarehouseDto[];
  canMoveBetweenWarehouses: boolean;
  quantity: string;
  onQuantityChange: (value: string) => void;
  qtyError: string | null;
  disabled: boolean;
}

export function TransferItemFields({
  lots,
  usedLotIds,
  lotId,
  onLotChange,
  lotTriggerRef,
  lot,
  hasSku,
  fromWarehouseName,
  mode,
  onModeChange,
  binId,
  onBinChange,
  toWarehouseId,
  onWarehouseChange,
  bins,
  fromWarehouseId,
  targetWarehouses,
  canMoveBetweenWarehouses,
  quantity,
  onQuantityChange,
  qtyError,
  disabled,
}: Props) {
  return (
    <>
      <WmsFieldLabel label="Партия">
        <Combobox
          triggerRef={lotTriggerRef}
          options={lots.map((l) => ({
            value: l.lotId,
            label: l.batchNumber,
            description: formatTransferLot(l),
            // Одна партия — одна строка документа (партиал-юник на сервере).
            disabled: usedLotIds.includes(l.lotId),
          }))}
          value={lotId || null}
          onChange={(next) => onLotChange(next ?? "")}
          placeholder={hasSku ? "Выбрать партию…" : "— сначала выберите фасовку —"}
          searchPlaceholder="Номер партии"
          emptyText="Партий с остатком нет"
          disabled={!hasSku}
          className="min-h-[56px]"
        />
      </WmsFieldLabel>

      <WmsFieldLabel label="Откуда">
        <div className="flex min-h-[56px] items-center rounded-xl bg-surface-low px-3 text-sm text-on-surface-variant ring-ghost">
          {fromWarehouseName}
          {lot ? ` · ${lot.binCode ?? "без ячейки"}` : ""}
        </div>
      </WmsFieldLabel>

      <TransferTargetFields
        mode={mode}
        onModeChange={onModeChange}
        binId={binId}
        onBinChange={onBinChange}
        warehouseId={toWarehouseId}
        onWarehouseChange={onWarehouseChange}
        bins={bins}
        fromWarehouseId={fromWarehouseId}
        targetWarehouses={targetWarehouses}
        canMoveBetweenWarehouses={canMoveBetweenWarehouses}
        disabled={disabled}
      />

      <TransferQuantityField
        intraWarehouse={mode === "BIN"}
        value={quantity}
        lotQuantity={lot?.quantity ?? null}
        error={qtyError}
        onChange={onQuantityChange}
      />
    </>
  );
}
